import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  Check,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Package,
  Users,
  Zap,
  MessageSquare,
  Layers,
  Grid3X3,
  FileText,
  Wand2,
  RotateCcw,
  Pencil,
  Save,
  Loader2,
} from "lucide-react";
import { type UseCampaignWizardReturn } from "@/hooks/useCampaignWizard";
import { WIZARD_STEPS, type WizardStepId } from "@/lib/campaign-draft";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Brand } from "@shared/schema";
import { ProductStep } from "./product-step";
import { PersonaStep } from "./persona-step";
import { NarrativeOpportunityStep } from "./narrative-opportunity-step";
import { NarrativeAnglesStep } from "./narrative-angles-step";
import { FunnelStrategyStep } from "./funnel-strategy-step";
import { AdUnitSelectionStep } from "./ad-unit-selection-step";
import { CampaignPlanStep } from "./campaign-plan-step";
import { CreativeBriefsStep } from "./creative-briefs-step";

const STEP_ICONS: Record<WizardStepId, typeof Package> = {
  product: Package,
  persona: Users,
  narrativeOpportunity: Zap,
  narrativeAngles: MessageSquare,
  funnelStrategy: Layers,
  mediaMix: Layers,
  adUnitSelections: Grid3X3,
  campaignPlan: FileText,
  creativeBriefs: Wand2,
};

function getStackValue(draft: UseCampaignWizardReturn["draft"], stepId: WizardStepId): string | undefined {
  switch (stepId) {
    case "product":
      return draft.product?.name;
    case "persona":
      return draft.persona?.name;
    case "narrativeOpportunity":
      return draft.narrativeOpportunity?.title;
    case "narrativeAngles":
      return draft.narrativeAngles.length > 0
        ? `${draft.narrativeAngles.length} selected`
        : undefined;
    case "funnelStrategy":
      return draft.funnelStrategy && draft.funnelStrategy.stages
        ? `${draft.funnelStrategy.stages.length} stages`
        : undefined;
    case "mediaMix":
      return draft.mediaMix
        ? `${draft.mediaMix.totalUnits} units`
        : undefined;
    case "adUnitSelections": {
      const total = draft.mediaMix?.totalUnits ?? draft.mediaMix?.staticUnits;
      const selectedCount = draft.adUnitSelections.length;
      if (!total && selectedCount === 0) return undefined;
      return `${total ?? 0} (${selectedCount} selected)`;
    }
    case "campaignPlan":
      if (!draft.campaignPlan) return undefined;
      return draft.campaignPlan.name || draft.campaignPlan.status || "Generated";
    case "creativeBriefs":
      return draft.creativeBriefs.completedCount > 0
        ? `${draft.creativeBriefs.completedCount} briefs`
        : draft.creativeBriefs.status !== "empty"
          ? draft.creativeBriefs.status
          : undefined;
    default:
      return undefined;
  }
}

interface CampaignWizardProps {
  wizard: UseCampaignWizardReturn;
  brandId: string;
}

export function CampaignWizard({ wizard, brandId }: CampaignWizardProps) {
  const {
    draft,
    currentStepIndex,
    totalSteps,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    goToStep,
    isStepCompleted,
    isStepStale,
    resetDraft,
    completedStepCount,
    setCampaignName,
  } = wizard;

  const { data: brand } = useQuery<Brand>({
    queryKey: [`/api/brands/${brandId}`],
    enabled: !!brandId,
  });

  const progressPercent = ((currentStepIndex + 1) / totalSteps) * 100;

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState(draft.name);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const escapePressedRef = useRef(false);
  const saveHandlerRef = useRef<{ save: () => Promise<void>; canSave: boolean; isSaving: boolean } | null>(null);
  const [saveState, setSaveState] = useState<{ canSave: boolean; isSaving: boolean }>({ canSave: false, isSaving: false });
  const isLastStep = draft.currentStep === "creativeBriefs";
  const handleRegisterSave = useCallback((handler: { save: () => Promise<void>; canSave: boolean; isSaving: boolean }) => {
    saveHandlerRef.current = handler;
    setSaveState({ canSave: handler.canSave, isSaving: handler.isSaving });
  }, []);

  const startEditing = useCallback(() => {
    setEditingName(draft.name);
    escapePressedRef.current = false;
    setIsEditingName(true);
    setTimeout(() => nameInputRef.current?.select(), 0);
  }, [draft.name]);

  const handleBlur = useCallback(() => {
    if (escapePressedRef.current) {
      escapePressedRef.current = false;
      setEditingName(draft.name);
    } else {
      const trimmed = editingName.trim();
      if (trimmed) {
        setCampaignName(trimmed);
      }
    }
    setIsEditingName(false);
  }, [editingName, setCampaignName, draft.name]);

  return (
    <div className="flex flex-col h-full" data-testid="campaign-wizard">
      <div className="shrink-0 border-b px-4 py-3" data-testid="wizard-progress-header">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    nameInputRef.current?.blur();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    escapePressedRef.current = true;
                    nameInputRef.current?.blur();
                  }
                }}
                className="text-base font-bold tracking-tight w-full bg-transparent border-b-2 border-primary outline-none px-0 py-0"
                data-testid="input-campaign-name"
              />
            ) : (
              <h1
                className="text-base font-bold tracking-tight truncate cursor-pointer group flex items-center gap-1.5 hover:text-primary transition-colors focus:text-primary focus:outline-none"
                onClick={startEditing}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); startEditing(); } }}
                tabIndex={0}
                role="button"
                data-testid="text-campaign-name"
                title="Click to rename"
              >
                {draft.name}
                <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
              </h1>
            )}
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-muted-foreground">
                Step {currentStepIndex + 1} of {totalSteps}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {completedStepCount}/{totalSteps} complete
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowResetConfirm(true)}
              data-testid="button-reset-draft"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Reset
            </Button>
          </div>
        </div>
        <div className="mt-2 h-1 w-full rounded-full bg-muted/30 overflow-hidden">
          <motion.div
            className="h-full bg-primary/50 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            data-testid="progress-bar-wizard"
          />
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="w-64 shrink-0 border-r" data-testid="wizard-campaign-stack">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-1">
              <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Campaign Stack
              </h3>

              {WIZARD_STEPS.map((step, index) => {
                const isActive = step.id === draft.currentStep;
                const completed = isStepCompleted(step.id);
                const rawStale = isStepStale(step.id);
                const stale = rawStale && (step.id !== "campaignPlan" || !!draft.campaignPlan);
                const Icon = STEP_ICONS[step.id];
                const value = getStackValue(draft, step.id);

                return (
                  <button
                    key={step.id}
                    onClick={() => goToStep(step.id)}
                    className={`w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all ${
                      isActive
                        ? "bg-primary/[0.08] ring-1 ring-primary/20"
                        : "hover:bg-muted/40"
                    }`}
                    data-testid={`button-step-${step.id}`}
                  >
                    <div
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md mt-0.5 ${
                        completed && !stale
                          ? "bg-primary/15"
                          : isActive
                          ? "bg-primary/10"
                          : value
                          ? "bg-primary/10"
                          : "bg-muted"
                      }`}
                    >
                      {completed && !stale ? (
                        <Check className="h-3 w-3 text-primary" />
                      ) : (
                        <Icon className={`h-3 w-3 ${
                          isActive || value ? "text-primary" : "text-muted-foreground"
                        }`} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <span className={`text-[10px] uppercase tracking-wider ${
                          isActive
                            ? "text-primary font-semibold"
                            : "text-muted-foreground font-medium"
                        }`}>
                          {step.label}
                        </span>
                        {stale && value && (
                          <AlertTriangle className="h-2.5 w-2.5 text-yellow-500 shrink-0" />
                        )}
                      </div>
                      {value ? (
                        <p className="text-xs font-medium truncate mt-0.5">{value}</p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground/50 italic mt-0.5">Not set</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <div className="flex-1 min-w-0 flex flex-col" data-testid="wizard-main-panel">
          <ScrollArea className="flex-1">
            <div className="p-6 max-w-2xl mx-auto">
              <WizardContextStrip stepId={draft.currentStep} wizard={wizard} />
              <StepContent
                stepId={draft.currentStep}
                wizard={wizard}
                brandId={brandId}
                brand={brand ?? null}
                onRegisterSave={handleRegisterSave}
              />
            </div>
          </ScrollArea>

          <div className="shrink-0 border-t px-6 py-3 flex items-center justify-between gap-4" data-testid="wizard-navigation">
            <Button
              variant="outline"
              onClick={goBack}
              disabled={!canGoBack}
              data-testid="button-go-back"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            {isLastStep ? (
              <Button
                className="rounded-full px-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => saveHandlerRef.current?.save()}
                disabled={!saveState.canSave}
                data-testid="button-save-campaign"
              >
                {saveState.isSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {saveState.isSaving ? "Saving..." : "Save Campaign"}
              </Button>
            ) : (
              <Button
                onClick={goForward}
                disabled={!canGoForward}
                data-testid="button-go-forward"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-reset-confirm">
          <DialogHeader>
            <DialogTitle>Reset campaign draft?</DialogTitle>
            <DialogDescription>
              This will clear all your progress — persona, product, narrative selections, strategies, and creative briefs. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowResetConfirm(false)}
              data-testid="button-reset-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowResetConfirm(false);
                resetDraft();
              }}
              data-testid="button-reset-confirm"
            >
              Reset Everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StepContent({
  stepId,
  wizard,
  brandId,
  brand,
  onRegisterSave,
}: {
  stepId: WizardStepId;
  wizard: UseCampaignWizardReturn;
  brandId: string;
  brand: Brand | null;
  onRegisterSave?: (handler: { save: () => Promise<void>; canSave: boolean; isSaving: boolean }) => void;
}) {
  switch (stepId) {
    case "product":
      return (
        <ProductStep
          brandId={brandId}
          selectedProduct={wizard.draft.product}
          onSelect={wizard.setProduct}
          selectedVariantName={wizard.draft.selectedVariantName ?? null}
          onVariantChange={wizard.setSelectedVariantName}
        />
      );
    case "persona":
      return (
        <PersonaStep
          brandId={brandId}
          selectedPersona={wizard.draft.persona}
          selectedProduct={wizard.draft.product}
          onSelect={wizard.setPersona}
        />
      );
    case "narrativeOpportunity":
      return (
        <NarrativeOpportunityStep
          brandId={brandId}
          productId={wizard.draft.product?.id ?? null}
          personaId={wizard.draft.persona?.id ?? null}
          selectedOpportunity={wizard.draft.narrativeOpportunity}
          onSelect={wizard.setNarrativeOpportunity}
          hasProduct={wizard.draft.product !== null}
          hasPersona={wizard.draft.persona !== null}
        />
      );
    case "narrativeAngles":
      return (
        <NarrativeAnglesStep
          brandId={brandId}
          generationId={wizard.draft.narrativeOpportunity?.generationId ?? null}
          selectedAngles={wizard.draft.narrativeAngles}
          onSelect={wizard.setNarrativeAngles}
          hasOpportunity={wizard.draft.narrativeOpportunity !== null}
          opportunityTitle={wizard.draft.narrativeOpportunity?.title ?? null}
        />
      );
    case "funnelStrategy":
      return (
        <FunnelStrategyStep
          brandId={brandId}
          brand={brand ?? null}
          product={wizard.draft.product}
          persona={wizard.draft.persona}
          narrativeOpportunity={wizard.draft.narrativeOpportunity}
          narrativeAngles={wizard.draft.narrativeAngles}
          funnelStrategy={wizard.draft.funnelStrategy}
          onUpdate={wizard.setFunnelStrategy}
          isStale={wizard.isStepStale("funnelStrategy")}
          onClearStale={() => wizard.clearStale("funnelStrategy")}
        />
      );
    case "adUnitSelections":
      return (
        <AdUnitSelectionStep
          brandId={brandId}
          brand={brand ?? null}
          draft={wizard.draft}
          adUnitSelections={wizard.draft.adUnitSelections}
          adUnitPlan={wizard.draft.adUnitPlan}
          funnelStrategy={wizard.draft.funnelStrategy}
          mediaMix={wizard.draft.mediaMix}
          mediaAllocation={wizard.draft.mediaAllocation}
          onUpdateSelections={wizard.setAdUnitSelections}
          onUpdatePlan={wizard.setAdUnitPlan}
          onUpdateMediaMix={wizard.setMediaMix}
          onUpdateMediaAllocation={wizard.setMediaAllocation}
          isStale={wizard.isStepStale("adUnitSelections")}
          onClearStale={() => wizard.clearStale("adUnitSelections")}
        />
      );
    case "campaignPlan":
      return (
        <CampaignPlanStep
          campaignPlan={wizard.draft.campaignPlan}
          plannerPersona={wizard.draft.plannerPersona}
          mediaAllocation={wizard.draft.mediaAllocation}
          adUnitSelections={wizard.draft.adUnitSelections}
          funnelStrategy={wizard.draft.funnelStrategy}
          mediaMix={wizard.draft.mediaMix}
          adUnitPlan={wizard.draft.adUnitPlan}
          campaignName={wizard.draft.name}
          product={wizard.draft.product}
          persona={wizard.draft.persona}
          narrativeOpportunity={wizard.draft.narrativeOpportunity}
          narrativeAngles={wizard.draft.narrativeAngles}
          brand={brand}
          onUpdate={wizard.setCampaignPlan}
          onUpdatePlannerPersona={wizard.setPlannerPersona}
          onUpdateMediaAllocation={wizard.setMediaAllocation}
          isStale={wizard.isStepStale("campaignPlan")}
          onClearStale={() => wizard.clearStale("campaignPlan")}
          onNext={wizard.forceGoForward}
        />
      );
    case "creativeBriefs":
      return (
        <CreativeBriefsStep
          creativeBriefs={wizard.draft.creativeBriefs}
          product={wizard.draft.product}
          selectedVariantName={wizard.draft.selectedVariantName ?? null}
          persona={wizard.draft.persona}
          brand={brand}
          funnelStrategy={wizard.draft.funnelStrategy}
          campaignPlan={wizard.draft.campaignPlan}
          plannerPersona={wizard.draft.plannerPersona}
          creativeStrategist={wizard.draft.creativeStrategist}
          adUnitSelections={wizard.draft.adUnitSelections}
          adUnitPlan={wizard.draft.adUnitPlan}
          narrativeAngles={wizard.draft.narrativeAngles}
          narrativeOpportunity={wizard.draft.narrativeOpportunity}
          mediaAllocation={wizard.draft.mediaAllocation}
          mediaMix={wizard.draft.mediaMix}
          onUpdate={wizard.setCreativeBriefs}
          onUpdateCreativeStrategist={wizard.setCreativeStrategist}
          onUpdateAdUnitSelections={wizard.setAdUnitSelections}
          onUpdateMediaAllocation={wizard.setMediaAllocation}
          isStale={wizard.isStepStale("creativeBriefs")}
          onClearStale={() => wizard.clearStale("creativeBriefs")}
          draftId={wizard.draft.id}
          draftName={wizard.draft.name}
          onRegisterSave={onRegisterSave}
          onResetDraft={wizard.resetDraft}
        />
      );
    default:
      return null;
  }
}

const STEP_ORDER: WizardStepId[] = [
  "product",
  "persona",
  "narrativeOpportunity",
  "narrativeAngles",
  "funnelStrategy",
  "adUnitSelections",
  "campaignPlan",
  "creativeBriefs",
];

function WizardContextStrip({
  stepId,
  wizard,
}: {
  stepId: WizardStepId;
  wizard: UseCampaignWizardReturn;
}) {
  const { draft } = wizard;
  const currentIndex = STEP_ORDER.indexOf(stepId);
  if (currentIndex <= 0) return null;

  type Item = { icon: typeof Package; label: string; value: string };
  const items: Item[] = [];

  if (currentIndex > STEP_ORDER.indexOf("product") && draft.product) {
    items.push({ icon: Package, label: "Product", value: draft.product.name });
  }
  if (currentIndex > STEP_ORDER.indexOf("persona") && draft.persona) {
    items.push({ icon: Users, label: "Persona", value: draft.persona.name });
  }
  if (
    currentIndex > STEP_ORDER.indexOf("narrativeOpportunity") &&
    draft.narrativeOpportunity
  ) {
    items.push({
      icon: Zap,
      label: "Narrative",
      value: draft.narrativeOpportunity.title,
    });
  }
  if (
    currentIndex > STEP_ORDER.indexOf("narrativeAngles") &&
    draft.narrativeAngles.length > 0
  ) {
    items.push({
      icon: MessageSquare,
      label: "Angles",
      value: `${draft.narrativeAngles.length} selected`,
    });
  }
  if (
    currentIndex > STEP_ORDER.indexOf("funnelStrategy") &&
    draft.funnelStrategy &&
    !Array.isArray(draft.funnelStrategy) &&
    Array.isArray(draft.funnelStrategy.stages)
  ) {
    items.push({
      icon: Layers,
      label: "Funnel",
      value: `${draft.funnelStrategy.stages.length} stages`,
    });
  }
  if (
    currentIndex > STEP_ORDER.indexOf("adUnitSelections") &&
    draft.adUnitSelections.length > 0
  ) {
    items.push({
      icon: Grid3X3,
      label: "Ad Units",
      value: `${draft.mediaMix?.totalUnits ?? draft.mediaMix?.staticUnits ?? 0} (${draft.adUnitSelections.length} selected)`,
    });
  }
  if (currentIndex > STEP_ORDER.indexOf("campaignPlan") && draft.campaignPlan) {
    items.push({ icon: FileText, label: "Plan", value: "Generated" });
  }

  if (items.length === 0) return null;

  return (
    <div
      className="mb-6 rounded-xl border border-border/40 bg-card px-4 py-3"
      data-testid="wizard-context-strip"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          Campaign Context
        </span>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className="flex items-center gap-2 min-w-0"
              data-testid={`context-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">
                  {item.label}
                </div>
                <div className="text-xs font-medium truncate max-w-[200px] leading-tight">
                  {item.value}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
